import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { t } from '../content';
import { canhBao, Glyph, hoaDang, kiem, sen, Spin } from '../kit/glyphs-core';
import { useUi } from './context';

const SYSTEM = new Set(['error.contextLost']);
const WARNING = new Set(['error.activity', 'error.sheet', 'fortune.motionDenied']);
const ICON: Readonly<Record<string, () => JSX.Element>> = {
  'fortune.saved': kiem,
  'keo.saved': kiem,
  'settings.cleared': kiem,
  'toast.renamed': kiem,
  'book.deleted': kiem,
  'prayer.doneLantern': hoaDang,
};
const SHOW_MS = 3200;

function ToastView(props: {
  kind: 'note' | 'warn' | 'sys';
  text: string;
  icon?: (() => JSX.Element) | undefined;
}) {
  return (
    <div
      class={props.kind === 'note' ? 'toast' : `toast ${props.kind}`}
      role={props.kind === 'warn' ? 'alert' : 'status'}
    >
      {props.kind === 'sys' ? (
        <Spin />
      ) : (
        <Glyph of={props.kind === 'warn' ? canhBao : (props.icon ?? sen)} size={22} />
      )}
      <span>{props.text}</span>
    </div>
  );
}

// Warnings stay until the situation changes (another zone or ritual); notes fade after a moment.
export function Toast(): JSX.Element {
  const { store } = useUi();
  const toast = store.toast.value;
  const zone = store.zoneId.value;
  const activity = store.activeActivity.value;
  const persistentWarning = !store.persistent.value && store.phase.value === 'play';
  const kind = !toast
    ? 'note'
    : SYSTEM.has(toast.key)
      ? 'sys'
      : WARNING.has(toast.key)
        ? 'warn'
        : 'note';

  useEffect(() => {
    if (!toast || kind !== 'note') return;
    const id = toast.id;
    const timer = setTimeout(() => {
      if (store.toast.value?.id === id) store.toast.value = null;
    }, SHOW_MS);
    return () => clearTimeout(timer);
  }, [toast, kind]);

  const clearWarning = (): void => {
    const current = store.toast.peek();
    if (current && WARNING.has(current.key)) store.toast.value = null;
  };
  useEffect(clearWarning, [zone]);
  useEffect(() => {
    if (activity !== null) clearWarning();
  }, [activity]);

  return (
    <div class="toast-stack" aria-live="polite">
      {persistentWarning && <ToastView kind="warn" text={t('storage.unavailable')} />}
      {toast && (
        <ToastView
          key={toast.id}
          kind={kind}
          text={t(toast.key, toast.vars)}
          icon={ICON[toast.key]}
        />
      )}
    </div>
  );
}
